import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { SDLC_PATH } from '../../config';
import type { NearbyMethodsProps } from './types';

/**
 * The other methods under the same process, as a row of links.
 *
 * **A method is a leaf, so this is the only sideways move on the page.** Every
 * other link goes up — back to the process, out to Confluence — and without
 * this, reading two neighbouring methods means going back up a level and down
 * again each time.
 *
 * **No row at all when a method is the only one under its process.** That is a
 * real case, and a heading with nothing under it is worse than the absence.
 */
export const NearbyMethods = ({
  methods,
  processLabel,
  processReference,
}: NearbyMethodsProps) => {
  if (methods.length === 0) {
    return null;
  }

  return (
    <Box component="section">
      <Typography variant="h3" component="h2">
        Nearby in {processReference} {processLabel}
      </Typography>

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          marginTop: 4,
          display: 'flex',
          flexWrap: 'wrap',
          // Wide enough to read as separate links without a separator glyph
          // between them, which is what the designs use instead of bullets.
          columnGap: 8,
          rowGap: 3,
        }}
      >
        {methods.map((method) => (
          <Box component="li" key={method.id}>
            <Typography
              variant="body2"
              component="span"
              sx={{ color: 'text.link', '&:hover': { textDecoration: 'underline' } }}
            >
              <RouterLink
                to={`${SDLC_PATH}/${method.path}`}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {method.name}
              </RouterLink>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
